// ═══════════════════════════════════════════════════════════════
// ENGAGEMENT ALERTS WIDGET
// Path: src/components/dashboard/EngagementAlerts.jsx
//
// "Relationships that need attention" — surfaces donors with:
//   - High giving but low engagement (authorization at risk)
//   - No inbound contact in 60+ days (drifting)
//   - Score dropped to zero (gone cold)
//
// Grounded in NECTAR: authorization is measured by inbound
// investment, not outbound effort. Decay forces honesty.
// ═══════════════════════════════════════════════════════════════

import React, { useMemo } from "react";
import { AlertTriangle, Clock, TrendingDown, DollarSign, X } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { calculateEngagement, getMemberActivities } from "../../utils/engagementScoring";

const ALERT_TYPES = {
  high_value_cold: {
    label: "High Value, Low Engagement",
    icon: DollarSign,
    color: "#dc2626",
    description: "Giving but disengaging — authorization at risk",
  },
  drifting: {
    label: "Drifting",
    icon: Clock,
    color: "#f59e0b",
    description: "No inbound contact in 60+ days",
  },
  gone_cold: {
    label: "Gone Cold",
    icon: TrendingDown,
    color: "#6b7280",
    description: "Engagement score dropped to zero",
  },
};

export const EngagementAlerts = ({ onClose } = {}) => {
  const { palette, members, navigate, allGifts, allEventAttendance, allSponsorships } = useApp();

  const alerts = useMemo(() => {
    const results = [];

    members.forEach((m) => {
      if (m.pathway === "prospect") return; // Skip prospects
      const engagement = calculateEngagement(m, undefined, getMemberActivities(m.id, allGifts, allEventAttendance, allSponsorships));
      const score = engagement.score;
      const days = engagement.daysSinceContact;

      // High value + low engagement: donor flag set, gave money, but score < 15
      if (m.isDonor && score < 15 && score >= 0) {
        // Check if they have meaningful giving history
        const hasGiving = m.totalGiven > 1000 || m.giftCount > 3;
        if (hasGiving) {
          results.push({
            member: m,
            type: "high_value_cold",
            score,
            days,
            sortWeight: 0, // highest priority
          });
          return; // Don't double-alert
        }
      }

      // Gone cold: score is exactly 0 with contact history
      if (score === 0 && engagement.totalContacts > 0) {
        results.push({
          member: m,
          type: "gone_cold",
          score,
          days,
          sortWeight: 2,
        });
        return;
      }

      // Drifting: no contact in 60+ days but has some engagement
      if (days !== null && days >= 60 && score > 0) {
        results.push({
          member: m,
          type: "drifting",
          score,
          days,
          sortWeight: 1,
        });
      }
    });

    // Sort: high value cold first, then drifting (by days desc), then gone cold
    results.sort((a, b) => {
      if (a.sortWeight !== b.sortWeight) return a.sortWeight - b.sortWeight;
      return (b.days || 0) - (a.days || 0);
    });

    return results.slice(0, 8); // Show top 8
  }, [members]);

  const typeCounts = useMemo(() => {
    const counts = { high_value_cold: 0, drifting: 0, gone_cold: 0 };
    members.forEach((m) => {
      if (m.pathway === "prospect") return;
      const engagement = calculateEngagement(m, undefined, getMemberActivities(m.id, allGifts, allEventAttendance, allSponsorships));
      const score = engagement.score;
      const days = engagement.daysSinceContact;

      if (m.isDonor && score < 15 && (m.totalGiven > 1000 || m.giftCount > 3)) {
        counts.high_value_cold++;
      } else if (score === 0 && engagement.totalContacts > 0) {
        counts.gone_cold++;
      } else if (days !== null && days >= 60 && score > 0) {
        counts.drifting++;
      }
    });
    return counts;
  }, [members]);

  return (
    <div
      style={{
        background: palette.cardBg,
        border: `1px solid ${palette.cardBorder}`,
        borderRadius: 10,
        padding: "14px 16px",
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }}
    >
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <AlertTriangle size={ICON_SIZES.sm} style={{ color: "#f59e0b" }} />
          <span
            style={{
              fontSize: FONT_SIZES.sm,
              fontWeight: 700,
              color: palette.text1,
              fontFamily: FONT_FAMILY,
            }}
          >
            Engagement Alerts
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {Object.entries(ALERT_TYPES).map(([key, type]) => (
            typeCounts[key] > 0 && (
              <span
                key={key}
                style={{
                  fontSize: "0.55rem",
                  fontWeight: 600,
                  padding: "2px 6px",
                  borderRadius: 6,
                  background: `${type.color}15`,
                  color: type.color,
                  fontFamily: FONT_FAMILY,
                }}
              >
                {typeCounts[key]} {type.label.split(",")[0]}
              </span>
            )
          ))}
          {onClose && (
            <button
              onClick={onClose}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 2,
                display: "flex",
                alignItems: "center",
                marginLeft: 4,
              }}
            >
              <X size={ICON_SIZES.sm} style={{ color: palette.text3 }} />
            </button>
          )}
        </div>
      </div>

      {/* Alert list */}
      {alerts.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "20px 0",
            color: palette.text3,
            fontSize: FONT_SIZES.xs,
            fontFamily: FONT_FAMILY,
          }}
        >
          No engagement alerts — all relationships are active
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {alerts.map((alert) => {
            const type = ALERT_TYPES[alert.type];
            const TypeIcon = type.icon;
            const m = alert.member;
            const name = m.lastName
              ? `${m.lastName}, ${m.firstName || ""}`
              : m.organization || m.firstName || "Unknown";

            return (
              <div
                key={m.id}
                onClick={() => navigate("memberDetail", m)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "6px 8px",
                  borderRadius: 6,
                  cursor: "pointer",
                  transition: "background 0.1s",
                  border: `1px solid ${type.color}15`,
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = `${type.color}06`}
                onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
              >
                <TypeIcon size={ICON_SIZES.xs} style={{ color: type.color, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: FONT_SIZES.xs,
                      fontWeight: 600,
                      color: palette.text1,
                      fontFamily: FONT_FAMILY,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {name}
                  </div>
                </div>
                <div
                  style={{
                    fontSize: "0.6rem",
                    color: palette.text3,
                    fontFamily: FONT_FAMILY,
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                  }}
                >
                  {alert.days !== null ? `${alert.days}d` : "—"}
                </div>
                <div
                  style={{
                    fontSize: "0.6rem",
                    fontWeight: 600,
                    color: type.color,
                    fontFamily: FONT_FAMILY,
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                  }}
                >
                  {alert.score}pts
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
